import { Injectable, signal, computed } from '@angular/core';
import { ISessionDetails } from '../../interfaces/session-details';
import { IBrandInfo } from '../../interfaces/brand/brand-info';

@Injectable({
  providedIn: 'root'
})
export class DataExchangeService {

  constructor() { }

  // Reactive signals for state management
  private authTokenSignal = signal<string | null>(null);
  private tempTokenSignal = signal<string | null>(null);
  private regionSignal = signal<string>('');
  private sourceSignal = signal<string>('');
  private brandCodeSignal = signal<string>('');
  private sessionSignal = signal<ISessionDetails | null>(null);
  private brandInfoSignal = signal<IBrandInfo | null>(null);
  private messageHeaderSignal = signal<string>('');
  private messageTextSignal = signal<string>('');
  private brandLogoImageSignal = signal<string>('');
  private nonmobileImageSignal = signal<string>('');

  // Public readonly signals for reactive access
  readonly authToken = this.authTokenSignal.asReadonly();
  readonly tempToken = this.tempTokenSignal.asReadonly();
  readonly region = this.regionSignal.asReadonly();
  readonly source = this.sourceSignal.asReadonly();
  readonly brandCode = this.brandCodeSignal.asReadonly();
  readonly session = this.sessionSignal.asReadonly();
  readonly brandInfo = this.brandInfoSignal.asReadonly();
  readonly messageHeader = this.messageHeaderSignal.asReadonly();
  readonly messageText = this.messageTextSignal.asReadonly();
  readonly brandLogoImage = this.brandLogoImageSignal.asReadonly();
  readonly nonmobileImage = this.nonmobileImageSignal.asReadonly();

  // Computed signals for derived state
  readonly isAuthenticated = computed(() => !!this.authTokenSignal());
  readonly hasValidSession = computed(() => !!this.sessionSignal());
  readonly hasBrandInfo = computed(() => !!this.brandInfoSignal());

  // Legacy getter methods for backward compatibility
  public getAuthToken(): string | null {
    return this.authTokenSignal();
  }

  public setAuthToken(authToken: string | null) {
    this.authTokenSignal.set(authToken);
  }

  public getTempToken(): string | null {
    return this.tempTokenSignal();
  }

  public setTempToken(tempToken: string | null) {
    this.tempTokenSignal.set(tempToken);
  }

  public getRegion(): string {
    return this.regionSignal();
  }

  public setRegion(region: string) {
    this.regionSignal.set(region);
  }

  public getSource(): string {
    return this.sourceSignal();
  }

  public setSource(source: string) {
    this.sourceSignal.set(source);
  }

  public getBrandCode(): string {
    return this.brandCodeSignal();
  }

  public setBrandCode(brandCode: string) {
    this.brandCodeSignal.set(brandCode);
  }

  public getSession(): ISessionDetails {
    const session = this.sessionSignal();
    if (!session) {
      throw new Error('Session not available. Ensure session is set before accessing.');
    }
    return session;
  }

  public setSession(session: ISessionDetails | null) {
    this.sessionSignal.set(session);
  }

  public getBrandInfo(): IBrandInfo {
    const brandInfo = this.brandInfoSignal();
    if (!brandInfo) {
      throw new Error('Brand info not available. Ensure brand info is set before accessing.');
    }
    return brandInfo;
  }

  public setBrandInfo(brandInfo: IBrandInfo | null) {
    this.brandInfoSignal.set(brandInfo);
  }

  public getMessageHeader(): string {
    return this.messageHeaderSignal();
  }

  public setMessageHeader(messageHeader: string) {
    this.messageHeaderSignal.set(messageHeader);
  }

  public getMessageText(): string {
    return this.messageTextSignal();
  }

  public setMessageText(messageText: string) {
    this.messageTextSignal.set(messageText);
  }

  public getBrandLogoImage(): string {
    return this.brandLogoImageSignal();
  }

  public setBrandLogoImage(brandLogoImage: string) {
    this.brandLogoImageSignal.set(brandLogoImage);
  }

  public getNonmobileImage(): string {
    return this.nonmobileImageSignal();
  }

  public setNonmobileImage(nonmobileImage: string) {
    this.nonmobileImageSignal.set(nonmobileImage);
  }

  public getConfigPropertyByName(propertyName: string): string | undefined {
    const brandInfo = this.brandInfoSignal();
    if (brandInfo?.configProperties) {
      return brandInfo.configProperties[propertyName] || '';
    }
    return '';
  }

  // New reactive methods for modern usage
  public updateAuthState(authToken: string | null, tempToken: string | null = null) {
    this.authTokenSignal.set(authToken);
    if (tempToken !== undefined) {
      this.tempTokenSignal.set(tempToken);
    }
  }

  public updateSessionState(session: ISessionDetails | null, brandCode?: string) {
    this.sessionSignal.set(session);
    if (brandCode) {
      this.brandCodeSignal.set(brandCode);
    }
  }

  public updateBrandState(brandInfo: IBrandInfo | null, brandCode?: string) {
    this.brandInfoSignal.set(brandInfo);
    if (brandCode) {
      this.brandCodeSignal.set(brandCode);
    }
  }

  public clearAuthState() {
    this.authTokenSignal.set(null);
    this.tempTokenSignal.set(null);
  }

  public clearSessionState() {
    this.sessionSignal.set(null);
    this.brandCodeSignal.set('');
    this.brandInfoSignal.set(null);
  }

  public clearMessages() {
    this.messageHeaderSignal.set('');
    this.messageTextSignal.set('');
  }
}
